import type { DatalayerOutput, GDDLEvent } from '@analytics/models';
import { inject, Injectable } from '@angular/core';
import { ConfigLoaderService } from '@core/config-angular';
import { PlatformService } from '@core/platform';
import { DomService, ScriptInjectOn, WINDOW } from '@innogy/utils-dom';

declare global {
  interface Window {
    jentis: any; // Jentis-specific global object to hold configuration and SDK methods
    _jts: any[]; // Jentis-specific array to queue events
  }
}

@Injectable({
  providedIn: 'root',
})
export class JentisService implements DatalayerOutput {
  readonly #platform = inject(PlatformService);
  readonly #windowRef = inject(WINDOW);
  readonly #domService = inject(DomService);
  readonly #config = inject(ConfigLoaderService).getConfigAsync();

  initialized = false;
  id = 'JENTIS';

  /**
   * Stub for dispatching events to Jentis; currently not implemented.
   */
  dispatchEvent(event: GDDLEvent) {
    if (this.initialized && event.event === 'page-impression') {
      this.#windowRef._jts.push({ track: 'pageview' });
    }
  }

  outputConditionsAreMet(): boolean {
    return this.#platform.isClient();
  }

  /**
   * Initializes the Jentis SDK if running on the client platform.
   * Sets up global configuration and pushes initial events into the queue.
   */
  async init() {
    if (await this.shouldLoad()) {
      this.#loadJentisSDK(
        'https://oo19nh.essent.nl',
        'essent-nl',
        await this.#getCDNWorkspace(),
        '_'
      );
      this.#windowRef._jts.push({ track: 'pageview' }); // Queue a pageview event
      this.#windowRef._jts.push({ track: 'submit' }); // Queue a form submission event
      this.initialized = true;
    }
  }

  async #getCDNWorkspace() {
    const { environment } = await this.#config;
    if (environment === 'prd') {
      return 'live';
    }
    return 'stage';
  }

  async shouldLoad() {
    const { environment, brand, segment } = await this.#config;

    return (
      this.#platform.isClient() &&
      brand === 'essent' &&
      segment === 'consument' &&
      (environment === 'staging' || environment === 'prd')
    );
  }

  /**
   * Loads the Jentis SDK by dynamically injecting a script tag with the appropriate configuration.
   * @param sCDN - CDN base URL
   * @param sCDNProject - Project identifier for Jentis
   * @param sCDNWorkspace - Workspace/environment identifier
   * @param sCDNVers - Version identifier, defaults to '_'
   */
  #loadJentisSDK(
    sCDN: string,
    sCDNProject: string,
    sCDNWorkspace: string,
    sCDNVers: string
  ) {
    // Check for preview version in session or local storage and override default version if present
    if (
      typeof this.#windowRef.localStorage?.getItem === 'function' &&
      typeof this.#windowRef.sessionStorage?.getItem === 'function'
    ) {
      sCDNVers =
        this.#windowRef.sessionStorage.getItem('jts_preview_version') ||
        this.#windowRef.localStorage.getItem('jts_preview_version') ||
        sCDNVers;
    }

    // Initialize the Jentis global object and its configuration if not already set
    this.#windowRef.jentis = this.#windowRef.jentis || {};
    this.#windowRef.jentis.config = this.#windowRef.jentis.config || {};
    this.#windowRef.jentis.config.frontend =
      this.#windowRef.jentis.config.frontend || {};

    // Configure Jentis frontend settings
    this.#windowRef.jentis.config.frontend.cdnhost = `${sCDN}/get/${sCDNWorkspace}/web/${sCDNVers}/`;
    this.#windowRef.jentis.config.frontend.vers = sCDNVers;
    this.#windowRef.jentis.config.frontend.env = sCDNWorkspace;
    this.#windowRef.jentis.config.frontend.project = sCDNProject;

    // Ensure the Jentis event queue exists
    this.#windowRef._jts = this.#windowRef._jts || [];

    // Inject the Jentis SDK script into the DOM
    this.#domService.injectScript({
      src: `${this.#windowRef.jentis.config.frontend.cdnhost}m0df4z.js`,
      async: true,
      injectOnPlatform: ScriptInjectOn.CLIENT_ONLY,
      injectInExperienceEditor: false,
      target: 'body',
    });
  }
}
